import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyReviews } from '../services/scheduleService';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';
import { HEALTH_COLORS, HEALTH_LABELS, formatSlip } from '../constants/schedule';

// Operations -> Weekly Review: the PM's monitored projects for this week,
// not-yet-submitted and worst health first (sorted by the backend).
const REVIEW_LABELS = { submitted: 'Submitted', draft: 'Draft saved' };

const WeeklyReviewListPage = () => {
  const [data, setData] = useState({ projects: [], week_start: null });
  const [loading, setLoading] = useState(true);
  const showToast = useUIStore((s) => s.showToast);

  useEffect(() => {
    getMyReviews()
      .then((res) => setData(res.data))
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load reviews', 'error'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = data.projects.filter((p) => p.is_key && p.review_status !== 'submitted').length; // only Key reviews are required

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-white">Weekly Review</h1>
        <p className="text-sm text-white/80 mt-1 mb-6">
          {data.week_start && `Week of ${formatDateDDMMYY(data.week_start)} · `}
          {data.projects.length} monitored project{data.projects.length === 1 ? '' : 's'}
          {pending > 0 && ` · ${pending} Key review${pending === 1 ? '' : 's'} to submit by Saturday 6 PM`}
        </p>

        {loading ? (
          <div className="glass-card p-6 text-sm text-gray-500">Loading...</div>
        ) : data.projects.length === 0 ? (
          <div className="glass-card p-6 text-sm text-gray-500">
            No monitored projects to review. A project appears here once its schedule is frozen as Rev 0 and Weekly
            monitoring is ticked, and you're its PM.
          </div>
        ) : (
          <div className="space-y-3">
            {data.projects.map((p) => (
              <div key={p.project_id} className="glass-card p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold text-gray-900">
                    {p.project_name}
                    {p.is_key && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-komal-blue text-white font-semibold">Key</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {p.client_name || '—'} · PM {p.pm_name || '—'} · handover {formatDateDDMMYY(p.forecast_handover)}{' '}
                    <span className={p.handover_slip_days > 0 ? 'text-red-700 font-semibold' : ''}>({formatSlip(p.handover_slip_days)})</span>
                  </div>
                  <div className="text-xs mt-1">
                    {p.departments_pending > 0
                      ? <span className="text-red-700">{p.departments_pending} of {p.departments_total} departments not updated</span>
                      : <span className="text-green-700">All departments updated</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.health && <span className={`text-xs px-2 py-1 rounded font-semibold ${HEALTH_COLORS[p.health]}`}>{HEALTH_LABELS[p.health]}</span>}
                  <span className={`text-xs px-2 py-1 rounded ${p.review_status === 'submitted' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                    {REVIEW_LABELS[p.review_status] || (p.is_key ? 'Not started · required' : 'Optional')}
                  </span>
                  <Link to={`/schedule/reviews/${p.project_id}`}
                    className="px-3 py-1.5 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">
                    Review
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyReviewListPage;
